import React, { Fragment, useCallback, useState } from "react";
import { withTranslation } from "react-i18next";
import Empty from "components/Empty";
import Loader from "components/Loader";
import { Link } from "react-router-dom";
import { formatLocalDateTime, handleError, handleSuccess } from "utils";
import moment from "moment";
import styles from "../reportedResponses.module.scss";
import SortDown from "../../../../../../assets/images/pms-icons/sort_down.svg";
import SortUp from "../../../../../../assets/images/pms-icons/sort_up.svg";
import Table from "components/table";
import "../reportedResponses.scss";
import constants from "../../../../../../constants";
import useHandleApiError from "hooks/useHandleApiError";
import {
  archiveOrUnarchiveChatHistory,
  archiveOrUnarchiveChatReponseRating,
  archiveOrUnarchiveResponseReported,
  useGetChatSessionList,
  useGetRatingsAndReviews,
  useGetResponseReportedList,
} from "repositories/ai-assistant-repository";
import { debounce } from "lodash";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import {
  Button,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  UncontrolledDropdown,
} from "reactstrap";
import ThreeDots from "../../../../../../assets/images/threedots.svg";
import ConfirmationModal from "components/CustomModal/ConfirmationModal";

const PAGE_SIZE = constants.pageSize;
const reportOptions = constants.reportOptions;

const Ongoing = ({ t }) => {
  const [pageNumber, setPageNumber] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedId, setSelectedId] = useState();

  const {
    isLoading,
    data,
    refetch,
    isFetching,
    error: isError,
  } = useGetResponseReportedList(pageNumber, PAGE_SIZE, searchTerm, false);
  useHandleApiError(isLoading, isFetching, isError);

  const history = useHistory();

  const handleSearchFilter = (event) => {
    const value = event.target.value;

    if (value.length >= 3 || value.length === 0) {
      const trimmedValue = value ? value.trim() : "";
      setSearchTerm(trimmedValue);
      setPageNumber(1);
    }
  };

  const optimisedSearch = useCallback(debounce(handleSearchFilter, 500), []);

  const [sort, setSort] = useState({});

  const viewDetails = (detail) => {
    history.push({
      pathname: constants.routes.aiAssistant.reportedResponseView,
      state: detail,
    });
  };

  const handleArchive = async () => {
    try {
      const result = await archiveOrUnarchiveResponseReported(selectedId, true);
      handleSuccess(result?.message);
      setIsModalOpen(false);
      refetch();
    } catch (error) {
      handleError(error);
    }
  };

  const columns = [
    {
      attrs: { datatitle: t("aiAssistant.ratingReviews.username") },
      dataField: "userFullName",
      text: t("aiAssistant.ratingReviews.username"),
      sort: true,

      headerFormatter: (column, colIndex, { sortElement }) => (
        <label className="mb-0 d-flex align-items-center">
          <span className="py-1 pms_table_heading">{column.text}</span>
          <span className="ms-1 d-inline-flex align-items-center">
            {sortElement}
          </span>
        </label>
      ),

      sortCaret: (order) => {
        if (!order) {
          return (
            <img src={SortDown} alt="sort_default" className="sort_icon" />
          );
        }
        if (order === "asc") {
          return <img src={SortUp} alt="sort_up" className="sort_icon" />;
        }

        return <img src={SortDown} alt="sort_down" className="sort_icon" />;
      },

      formatter: (cellContent, row) => {
        if (!isLoading)
          return row?.user?.firstName ? (
            <span className="pms_table_data">
              {row?.user?.firstName} {row?.user?.lastName}
            </span>
          ) : (
            "--"
          );
        return <div className="text-placeholder-150 shimmer-animation"></div>;
      },
    },
    {
      attrs: { datatitle: t("aiAssistant.ratingReviews.accountOwner") },
      dataField: "accountOwnerName",
      text: t("aiAssistant.ratingReviews.accountOwner"),
      sort: true,

      // Place the caret inside the label
      headerFormatter: (column, colIndex, { sortElement }) => (
        <label className="mb-0 d-flex align-items-center">
          <span className="py-1 pms_table_heading">{column.text}</span>
          <span className="ms-1 d-inline-flex align-items-center">
            {sortElement}
          </span>
        </label>
      ),

      // Customize caret images
      sortCaret: (order) => {
        if (!order) {
          return (
            <img src={SortDown} alt="sort_default" className="sort_icon" />
          );
        }
        if (order === "asc") {
          return <img src={SortUp} alt="sort_up" className="sort_icon" />;
        }
        return <img src={SortDown} alt="sort_down" className="sort_icon" />;
      },

      formatter: (cellContent, row) => {
        if (!isLoading)
          return row?.owner?.firstName ? (
            <span className="pms_table_data">
              {row?.owner?.firstName} {row?.owner?.lastName}
            </span>
          ) : (
            "--"
          );
        return <div className="text-placeholder-150 shimmer-animation"></div>;
      },
    },
    {
      attrs: { datatitle: t("aiAssistant.ratingReviews.emailAddresss") },
      dataField: "userEmailId",
      text: t("aiAssistant.ratingReviews.emailAddresss"),
      sort: true,

      headerFormatter: (column, colIndex, { sortElement }) => (
        <label className="mb-0 d-flex align-items-center">
          <span className="py-1 pms_table_heading">{column.text}</span>
          <span className="ms-1 d-inline-flex align-items-center">
            {sortElement}
          </span>
        </label>
      ),

      sortCaret: (order) => {
        if (!order) {
          return (
            <img src={SortDown} alt="sort_default" className="sort_icon" />
          );
        }
        if (order === "asc") {
          return <img src={SortUp} alt="sort_up" className="sort_icon" />;
        }
        return <img src={SortDown} alt="sort_down" className="sort_icon" />;
      },
      formatter: (cellContent, row) => {
        if (!isLoading)
          return row?.user?.emailId ? (
            <span className="pms_table_data">{row?.user?.emailId || "--"}</span>
          ) : (
            "--"
          );
        return <div className="text-placeholder-150 shimmer-animation"></div>;
      },
    },
    {
      attrs: { datatitle: t("aiAssistant.ratingReviews.date") },
      dataField: "createdAt",
      text: (
        <Fragment>
          <label className="mb-0">
            <span className="py-1 pms_table_heading">
              {t("aiAssistant.ratingReviews.date")}
            </span>
          </label>
        </Fragment>
      ),
      formatter: (cellContent) => {
        if (!isLoading)
          return cellContent
            ? formatLocalDateTime(cellContent, "MMM DD, YYYY")
            : "--";
        return <div className="text-placeholder-150 shimmer-animation"></div>;
      },
      sort: true,
      sortCaret: (order, column) => {
        if (order === "asc" || !order)
          return (
            <span>
              <img src={SortDown} alt="sort_down" className="sort_icon" />
            </span>
          );
        else if (order === "desc")
          return (
            <span>
              <img src={SortUp} alt="sort_up" className="sort_icon" />
            </span>
          );
        return null;
      },
    },
    {
      attrs: { datatitle: t("aiAssistant.ratingReviews.reason") },
      dataField: "reportType",
      text: t("aiAssistant.ratingReviews.reason"),
      sort: true,

      headerFormatter: (column, colIndex, { sortElement }) => (
        <label className="mb-0 d-flex align-items-center">
          <span className="py-1 pms_table_heading">{column.text}</span>
          <span className="ms-1 d-inline-flex align-items-center">
            {sortElement}
          </span>
        </label>
      ),

      sortCaret: (order) => {
        if (!order) {
          return (
            <img src={SortDown} alt="sort_default" className="sort_icon" />
          );
        }
        if (order === "asc") {
          return <img src={SortUp} alt="sort_up" className="sort_icon" />;
        }
        return <img src={SortDown} alt="sort_down" className="sort_icon" />;
      },
      formatter: (cellContent) => {
        if (!isLoading)
          return cellContent ? (
            <span className="pms_table_data truncate_text_line_clam_3">
              {reportOptions.find((option) => option.id === cellContent)
                ?.title || "--"}
            </span>
          ) : (
            "--"
          );
        return <div className="text-placeholder-150 shimmer-animation"></div>;
      },
    },
    {
      attrs: { datatitle: t("aiAssistant.ratingReviews.reviewStatus") },
      dataField: "reviewStatus",
      text: (
        <Fragment>
          <label className="mb-0">
            <span className="py-1 pms_table_heading">
              {t("aiAssistant.ratingReviews.reviewStatus")}
            </span>
          </label>
        </Fragment>
      ),
      formatter: (cellContent) => {
        if (!isLoading)
          return cellContent == "Seen" ? (
            <Button
              className={`appointment-table-custom-btn ${styles["review-status-btn"]}`}
            >
              <span>{cellContent}</span>
            </Button>
          ) : (
            "--"
          );
        return <div className="text-placeholder-150 shimmer-animation"></div>;
      },
    },
    {
      attrs: { datatitle: t("aiAssistant.ratingReviews.actions") },
      dataField: "action",
      text: (
        <Fragment>
          <label className="mb-0">
            <span className="py-1 pms_table_heading">
              {t("aiAssistant.ratingReviews.actions")}
            </span>
          </label>
        </Fragment>
      ),
      formatter: (cellContent, row) => {
        const menuOptions = [
          {
            text: t("view"),
            onclick: () => viewDetails(row),
          },
          {
            text: t("aiAssistant.archive"),
            onclick: () => {
              setSelectedId(row?.id);
              setIsModalOpen(true);
            },
          },
        ];

        return (
          <UncontrolledDropdown>
            <DropdownToggle caret={false} tag="div" className="cursor-pointer">
              <div className="ellipsis-icon">
                <img src={ThreeDots} alt="ThreeDots" />
              </div>
            </DropdownToggle>
            <DropdownMenu left className={styles["office-dropdown-menu"]}>
              {menuOptions.map((option, index) => (
                <DropdownItem
                  key={index}
                  className={styles["office-dropdown-item"]}
                >
                  {option.link ? (
                    <Link className="m-0" to={option.link}>
                      {option.text}
                    </Link>
                  ) : (
                    <p
                      className="m-0 cursor-pointer"
                      onClick={(e) => option?.onclick?.(e, row)}
                    >
                      {option.text}
                    </p>
                  )}
                </DropdownItem>
              ))}
            </DropdownMenu>
          </UncontrolledDropdown>
        );
      },
    },
  ];

  const handleTableChange = (
    type,
    { page, sizePerPage, sortField, sortOrder },
  ) => {
    if (type === "sort") {
      setSort({ sortField, sortOrder });
    }
  };

  let rows = [];
  let totalItems = 0;
  if (!isLoading && data && data?.items) {
    rows = data.items;
    totalItems = data?.totalCount;
  } else {
    rows = new Array(PAGE_SIZE).fill({});
    totalItems = PAGE_SIZE;
  }

  let updatedList = rows;
  if (sort && sort.sortField) {
    const { sortField, sortOrder } = sort;
    updatedList = updatedList.sort((a, b) => {
      const aValue = a[sortField];
      const bValue = b[sortField];
      if (sortOrder === "asc") {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });
  }

  const renderListing = () => (
    <div
      className={`table-td-last-50 timesheet-table test_dummy shadow-responsive ${styles["pms_doctors_table"]}`}
    >
      <div className="test-table-box-ai-assistant">
        <Table
          keyField="id"
          data={updatedList}
          columns={columns}
          defaultSortDirection={"desc"}
          handlePagination={(e) => setPageNumber(e)}
          pageNumber={pageNumber}
          totalItems={totalItems}
          pageSize={PAGE_SIZE}
          onTableChange={handleTableChange}
        />
      </div>
    </div>
  );

  return (
    <div className="staff-listing-timesheet pt-1 overflow-auto">
      {(isLoading || isFetching) && <Loader />}
      <div className="filter-section">
        <div className="search-box mb-4">
          <input
            name="searchTerm"
            type="text"
            placeholder={t("aiAssistant.search")}
            onInput={optimisedSearch}
          />
          <span className="ico">
            <img
              src={require("assets/images/search-icon.svg").default}
              alt="icon"
            />
          </span>
        </div>
      </div>
      {!!rows?.length ? (
        renderListing()
      ) : (
        <Empty Message={t("noDataAvaliableForSelectoion")} />
      )}
      {isModalOpen && (
        <ConfirmationModal
          isModalOpen={isModalOpen}
          closeModal={() => setIsModalOpen(false)}
          handleConfirm={handleArchive}
          title={t("aiAssistant.archiveReportTitle")}
          modalContent={t("aiAssistant.archiveReportContent")}
          modalSubContent={t("aiAssistant.archiveReportSubContent")}
          btnText={t("aiAssistant.archiveBtn")}
        />
      )}
    </div>
  );
};

export default withTranslation()(Ongoing);
